import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";

// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Divider,
  Button,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
// import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import AppConfig from "appConfig";
import usePageTitle from "app/pageTitle";
import MDInput from "theme/components/MDInput";
import { useAwaitDialog } from "app/components/status_dialog";
import { useAppLocation } from "agent_location_context";
import GeoTaggedCamera from "app/components/gps_camera";

export default function OpsEntityPage() {
  const pageTitle = "";
  usePageTitle(pageTitle);

  const { ref } = useParams();
  const navigate = useNavigate();

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);
  const { applocation, permissionStatus, updateAppLocation } = useAppLocation();

  const [entity, setEntity] = useState(null);
  const [activities, setActivities] = useState(null);
  const [visitCount, setVisitCount] = useState(0);
  const [salesCount, setSalesCount] = useState(0);
  const [salesRules, setSalesRules] = useState([]);

  const [visitPhoto, setVisitPhoto] = useState(null);
  const [openCamera, setOpenCamera] = useState(false);
  const [showCheckinDialog, setShowCheckinDialog] = useState(false);

  const [openSalesRulesDialog, setOpenSalesRulesDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`/agents/ops/entities/${ref}/fetch.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setEntity(result.data.data.entity);
            setActivities(result.data.data.activities);
            setVisitCount(result.data.data.visit_count);
            setSalesCount(result.data.data.sales_count);
            setSalesRules(result.data.data.sales_rules);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  async function checkIn() {
    if (!applocation) {
      setSeverity("error");
      setErrMsg(
        "GPS coords missing. Grant browser location permissions and try again.",
      );
      setOpen(true);
      return;
    }

    if (visitPhoto === null) {
      setSeverity("error");
      setErrMsg("Photo is required.");
      setOpen(true);
      return;
    }

    setShowCheckinDialog(false);
    setShowLoading(true);

    const json = {
      reference: ref,
      lat: applocation?.lat,
      lng: applocation?.lng,
      photo: visitPhoto.image,
    };

    await axios
      .post("/agents/ops/entities/checkin.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success");
          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Failed");
        }
      })
      .catch(async () => {
        const err = `Unable to complete request. ${
          navigator.onLine
            ? ""
            : "Make sure you have an active internet connection"
        }`;
        setShowLoading(false);
        await statusDialog.show(err, "error", "Failed");
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openSalesRulesDialog} fullWidth maxWidth="xs">
        <DialogTitle>Select Sales Type</DialogTitle>
        <DialogContent>
          <MDBox mb={5}>
            {salesRules.map((rule, idx) => (
              <MDBox>
                <MDBox
                  mt={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={()=>navigate(`/ops/workspaces/entities/sales/${ref}/${rule.reference}`)}
                >
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="regular">
                      {rule.title}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDButton size="small" color="primary" iconOnly>
                      <Icon>east</Icon>
                    </MDButton>
                  </MDBox>
                </MDBox>
                <Divider my={2} />
              </MDBox>
            ))}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="primary"
            startIcon={<Icon>cancel</Icon>}
            onClick={() => setOpenSalesRulesDialog(false)}
          >
            Cancel
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={showCheckinDialog} fullWidth maxWidth="xs">
        <DialogTitle>Check In</DialogTitle>
        <DialogContent>
          <MDBox mt={0} align="center">
            <GeoTaggedCamera
              open={openCamera}
              onClose={() => setOpenCamera(false)}
              onCapture={(data) => {
                setVisitPhoto(data);
                setOpenCamera(false);
              }}
            />
            <MDTypography mt={2} variant="h6">
              Take a selfie
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="primary"
            startIcon={<Icon>cancel</Icon>}
            onClick={() => setShowCheckinDialog(false)}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="primary"
            startIcon={<Icon>check</Icon>}
            onClick={() => checkIn()}
          >
            Check In
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={1} mb={5}>
        {pageLoaded && (
          <MDBox>
            <MDBox ml={-2}>
              <Button
                onClick={() => window.history.go(-1)}
                startIcon={<Icon>west</Icon>}
                color="dark"
              >
                Back
              </Button>
            </MDBox>
            <MDBox mt={1}>
              <Card>
                <MDBox p={2}>
                  <MDBox>
                    <ListItem>
                      <ListItemText
                        primary={
                          <MDTypography variant="h6">
                            {entity.title}
                          </MDTypography>
                        }
                        secondary={
                          <MDTypography variant="caption">
                            {entity.address}
                          </MDTypography>
                        }
                      />
                      <MDBox m={0}>
                        <MDTypography fontSize={32}>
                          <Icon>business</Icon>
                        </MDTypography>
                      </MDBox>
                    </ListItem>
                  </MDBox>
                  <Divider />
                  <MDBox
                    mt={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <MDBox>
                      <MDTypography
                        variant="h6"
                        fontSize={12}
                        fontWeight="regular"
                      >
                        City: {entity.city} | State: {entity.state} | LGA:{" "}
                        {entity.lga}
                      </MDTypography>
                    </MDBox>
                    {entity.phone_number && (
                      <MDBox>
                        <MDButton
                          size="small"
                          color="dark"
                          iconOnly
                          onClick={() =>
                            window.open(`tel:${entity.phone_number}`)
                          }
                        >
                          <Icon>call</Icon>
                        </MDButton>
                      </MDBox>
                    )}
                  </MDBox>
                  <MDBox></MDBox>
                </MDBox>
              </Card>
            </MDBox>
            {salesRules.length > 0 && (
              <MDBox mt={3}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox
                        p={1}
                        align="center"
                        onClick={() =>
                          navigate(
                            `/ops/workspaces/entities/sales/history/${ref}`,
                          )
                        }
                      >
                        <MDTypography variant="h6" fontSize={48}>
                          {salesCount}
                        </MDTypography>
                        <MDTypography variant="caption">
                          Sales Made
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox
                        p={1}
                        align="center"
                        onClick={() =>
                          // navigate(`/ops/workspaces/entities/sales/${ref}`)
                          setOpenSalesRulesDialog(true)
                        }
                      >
                        <MDTypography variant="h6" fontSize={48}>
                          {entity.currency}
                        </MDTypography>
                        <MDButton
                          color="success"
                          size="small"
                          variant="contained"
                        >
                          Sell
                        </MDButton>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              </MDBox>
            )}

            <MDBox mt={3}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Card sx={{ height: "100%" }}>
                    <MDBox p={1} align="center">
                      <MDTypography variant="h6" fontSize={48}>
                        {visitCount}
                      </MDTypography>
                      <MDTypography variant="caption">Visits Made</MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card sx={{ height: "100%" }}>
                    <MDBox
                      p={1}
                      align="center"
                      onClick={() => setShowCheckinDialog(true)}
                    >
                      <MDTypography variant="h6" fontSize={48}>
                        <Icon color="primary">where_to_vote</Icon>
                      </MDTypography>
                      <MDButton color="primary" size="small" variant="caption">
                        Check-In
                      </MDButton>
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            </MDBox>
            <MDBox mt={3}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6">Activities</MDTypography>
                  <Divider />
                  <MDBox mt={1}>
                    {activities.map((activity) => (
                      <MDBox
                        mb={1.5}
                        onClick={() =>
                          navigate(
                            `/ops/workspaces/entities/activity/${ref}/${activity.reference}`,
                          )
                        }
                      >
                        <ListItem>
                          <ListItemText
                            primary={
                              <MDTypography variant="body2" fontSize={15}>
                                {activity.title}
                              </MDTypography>
                            }
                          />
                          <MDBox m={0}>
                            <MDTypography fontSize={24}>
                              <Icon color="dark">east</Icon>
                            </MDTypography>
                          </MDBox>
                        </ListItem>
                      </MDBox>
                    ))}
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
